import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { money } from '../lib/format.js';
import { useEscape, useScrollLock } from '../lib/hooks.js';
import { useCart } from '../state/cart.js';
import { Alert, Badge, Button, EmptyState, Skeleton } from './ui.js';
/**
 * Basket drawer.
 *
 * Two behaviours worth noting.
 *
 * **Focus management.** When the drawer opens, focus moves to the close button
 * and the panel is marked `aria-modal`. Without that, a keyboard user tabs
 * straight past the drawer into the page behind it, which is invisible but still
 * focusable — the classic modal accessibility failure.
 *
 * **Optimistic quantity controls.** The stepper updates the server and the
 * authoritative cart replaces local state. It does not update local state first.
 * A basket is small enough that the round trip is imperceptible, and showing a
 * quantity that the server then rejects is worse than a 100 ms delay.
 */
export function CartDrawer() {
  const {
    cart,
    loading,
    mutating,
    error,
    notice,
    drawerOpen,
    closeDrawer,
    updateItem,
    removeItem,
    dismissNotice,
  } = useCart();

  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useScrollLock(drawerOpen);
  useEscape(closeDrawer, drawerOpen);

  useEffect(() => {
    if (drawerOpen) closeButtonRef.current?.focus();
  }, [drawerOpen]);

  if (!drawerOpen) return null;

  const items = cart?.items ?? [];
  const currency = cart?.currency ?? 'USD';
  const threshold = cart?.freeShippingThresholdMinor ?? 0;
  const subtotal = cart?.totals.subtotalMinor ?? 0;
  const remaining = Math.max(0, threshold - subtotal);
  const progress = threshold === 0 ? 100 : Math.min(100, (subtotal / threshold) * 100);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Your basket">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={closeDrawer}
        aria-label="Close basket"
        tabIndex={-1}
      />

      <div className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-line bg-surface shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <h2 className="text-base font-semibold">Your basket</h2>
            <p className="text-xs text-subtle">
              {cart?.itemCount ?? 0} item{(cart?.itemCount ?? 0) === 1 ? '' : 's'}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={closeDrawer}
            className="rounded-lg p-2 text-muted transition-colors hover:bg-raised hover:text-ink"
            aria-label="Close basket"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {threshold > 0 && items.length > 0 && (
          <div className="border-b border-line px-5 py-3">
            <p className="text-xs text-muted">
              {remaining > 0 ? (
                <>
                  <span className="font-medium text-ink">{money(remaining, currency)}</span> away from
                  free shipping
                </>
              ) : (
                <span className="font-medium text-positive">Free shipping unlocked</span>
              )}
            </p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sunken">
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-300"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progress towards free shipping"
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {notice && (
            <div className="mb-4">
              <Alert tone="warning" title="Basket adjusted" onDismiss={dismissNotice}>
                {notice}
              </Alert>
            </div>
          )}

          {error && (
            <div className="mb-4">
              <Alert tone="danger" title="Could not update your basket">
                {error}
              </Alert>
            </div>
          )}

          {loading ? (
            <ul className="space-y-4">
              {[0, 1, 2].map((index) => (
                <li key={index} className="flex gap-3">
                  <Skeleton className="size-20 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/4" />
                  </div>
                </li>
              ))}
            </ul>
          ) : items.length === 0 ? (
            <EmptyState
              title="Nothing here yet"
              description="Add something from the catalogue and it will show up in your basket straight away."
              action={
                <Button variant="secondary" size="sm" onClick={closeDrawer}>
                  Continue shopping
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {items.map((item) => (
                <li key={item.id} className="flex gap-3 py-4 first:pt-0">
                  <Link
                    to={`/product/${item.slug}`}
                    onClick={closeDrawer}
                    className="size-20 shrink-0 overflow-hidden rounded-lg border border-line bg-sunken"
                  >
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center text-[10px] text-subtle">
                        No image
                      </div>
                    )}
                  </Link>

                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/product/${item.slug}`}
                        onClick={closeDrawer}
                        className="truncate text-sm font-medium hover:text-accent"
                      >
                        {item.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => void removeItem(item.id)}
                        disabled={mutating}
                        className="shrink-0 rounded p-1 text-subtle transition-colors hover:text-danger disabled:opacity-50"
                        aria-label={`Remove ${item.name} from basket`}
                      >
                        <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.75">
                          <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>

                    {item.stockWarning && (
                      <p className="mt-1 text-xs text-warning">
                        Only {item.stock} left — quantity will be adjusted
                      </p>
                    )}

                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center rounded-lg border border-line">
                        <QuantityButton
                          label={`Decrease quantity of ${item.name}`}
                          disabled={mutating || item.quantity <= 1}
                          onClick={() => void updateItem(item.id, item.quantity - 1)}
                        >
                          −
                        </QuantityButton>
                        <span className="tabular w-8 text-center text-sm" aria-live="polite">
                          {item.quantity}
                        </span>
                        <QuantityButton
                          label={`Increase quantity of ${item.name}`}
                          disabled={mutating || item.quantity >= Math.min(item.stock, 20)}
                          onClick={() => void updateItem(item.id, item.quantity + 1)}
                        >
                          +
                        </QuantityButton>
                      </div>
                      <span className="tabular text-sm font-medium">
                        {money(item.lineTotalMinor, currency)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && cart && (
          <footer className="border-t border-line bg-raised px-5 py-4">
            <dl className="space-y-1.5 text-sm">
              <Row label="Subtotal" value={money(cart.totals.subtotalMinor, currency)} />
              <Row
                label="Shipping"
                value={
                  cart.totals.shippingMinor === 0
                    ? 'Free'
                    : money(cart.totals.shippingMinor, currency)
                }
              />
              <Row label="Tax" value={money(cart.totals.taxMinor, currency)} />
              <div className="flex items-center justify-between border-t border-line pt-2 text-base font-semibold">
                <dt>Total</dt>
                <dd className="tabular">{money(cart.totals.totalMinor, currency)}</dd>
              </div>
            </dl>

            <div className="mt-4 space-y-2">
              {/*
                A Link styled as a button, not a Button wrapping a Link. Nesting an
                anchor inside a <button> is invalid HTML, breaks middle-click and
                "open in new tab", and screen readers announce it as a button that
                happens to contain a link.
              */}
              <Link
                to="/checkout"
                onClick={closeDrawer}
                className="flex h-12 w-full items-center justify-center rounded-lg bg-accent px-6 text-base font-medium text-accent-ink transition-colors hover:bg-accent-hover"
              >
                Checkout
              </Link>
              <Badge tone="neutral" className="w-full justify-center py-1">
                Demonstration checkout — no card is charged
              </Badge>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

function QuantityButton({
  children,
  label,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 items-center justify-center text-muted transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}
